import { useEffect, useState } from 'react';
import { invokeFunction } from '@/integrations/supabase/invoke';
import { DUCKDB_VERSION, HARDCHARGER_VERSION, CHARGEWIRE_VERSION } from '@/data/hardcharger-launch';

/** The versions the Hardcharger server's startup banner prints. */
export interface HardchargerVersions {
  hardcharger: string; // 2.1.1
  chargewire: string; // 1.0.2
  duckdb: string; // v2.0.0
}

const CACHE_KEY = 'hardcharger-versions';
const CACHE_TTL = 1000 * 60 * 60; // 1 hour

interface CachedData {
  versions: HardchargerVersions;
  timestamp: number;
}

// The versions this build was made with: the prerendered HTML shows them, and they stay when the
// latest release can't be read.
const BUILT: HardchargerVersions = { hardcharger: HARDCHARGER_VERSION, chargewire: CHARGEWIRE_VERSION, duckdb: `v${DUCKDB_VERSION}` };

function valid(value: unknown): value is { version: string; chargewire: string; duckdb: string } {
  const v = value as Record<string, unknown> | null | undefined;
  return typeof v?.version === 'string' && /^\d+\.\d+\.\d+$/.test(v.version)
    && typeof v.chargewire === 'string' && /^\d+\.\d+\.\d+$/.test(v.chargewire)
    && typeof v.duckdb === 'string' && /^v\d+\.\d+\.\d+$/.test(v.duckdb);
}

/** The latest Hardcharger release's versions, from the github-stats Edge Function (as useGizmoSQLVersions). */
export function useHardchargerVersions(): HardchargerVersions {
  // Start from BUILT on the client too, so the first render matches the prerendered HTML.
  const [versions, setVersions] = useState<HardchargerVersions>(BUILT);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed: CachedData = JSON.parse(cached);
        // A visitor's cache from an older build may lack a field this one shows: fetch again then.
        const v = parsed.versions as Partial<HardchargerVersions> | undefined;
        const complete = typeof v?.hardcharger === 'string' && typeof v.chargewire === 'string' && typeof v.duckdb === 'string';
        if (complete && Date.now() - parsed.timestamp < CACHE_TTL) {
          setVersions(parsed.versions);
          return;
        }
      }
    } catch {
      // ignore
    }

    let cancelled = false;
    invokeFunction('github-stats')
      .then(({ data, error }) => {
        if (cancelled || error || !valid(data?.hardcharger)) return;
        const latest = data.hardcharger;
        const result: HardchargerVersions = { hardcharger: latest.version, chargewire: latest.chargewire, duckdb: latest.duckdb };
        setVersions(result);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ versions: result, timestamp: Date.now() } satisfies CachedData));
        } catch {
          // ignore
        }
      })
      .catch(() => {
        // Keep the built versions
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return versions;
}
